import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useInsuredContacts, useReplaceInsuredContacts, type InsuredContact } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneDisplay } from '@/lib/formatters';
import { ContactFormDialog } from './ContactFormDialog';

interface Props {
  insuredId: string;
}

export function ContactsSection({ insuredId }: Props) {
  const { data, isLoading } = useInsuredContacts(insuredId);
  const replace = useReplaceInsuredContacts(insuredId);
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);

  const contacts = data ?? [];

  const enforceSinglePrimary = (list: InsuredContact[]): InsuredContact[] => {
    let primarySeen = false;
    return list.map((c) => {
      const wants = c.isPrimary === true;
      const isPrimary = wants && !primarySeen;
      if (isPrimary) primarySeen = true;
      return { ...c, isPrimary };
    });
  };

  const writeContacts = (next: InsuredContact[]) => {
    replace.mutate(enforceSinglePrimary(next), {
      onSuccess: () => toast({ title: 'Contacts updated' }),
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to update contacts';
        toast({ title: 'Failed to update contacts', description: message, variant: 'destructive' });
      },
    });
  };

  const handleAddOrEditSubmit = (contact: InsuredContact) => {
    let next: InsuredContact[];
    if (editIdx === null) {
      const others = contact.isPrimary
        ? contacts.map((c) => ({ ...c, isPrimary: false }))
        : contacts.slice();
      next = [...others, contact];
    } else {
      next = contacts.slice();
      if (contact.isPrimary && next[editIdx].isPrimary !== true) {
        next = next.map((c) => ({ ...c, isPrimary: false }));
      }
      next[editIdx] = { ...next[editIdx], ...contact };
    }
    setDialogOpen(false);
    setEditIdx(null);
    writeContacts(next);
  };

  const handleDelete = () => {
    if (deleteIdx === null) return;
    const next = contacts.filter((_, i) => i !== deleteIdx);
    setDeleteIdx(null);
    writeContacts(next);
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Contacts</h3>
          <span className="text-xs text-muted-foreground">({contacts.length})</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          data-testid="contacts-add-button"
          onClick={() => { setEditIdx(null); setDialogOpen(true); }}
          className="cursor-pointer"
        >
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>

      {isLoading ? (
        <div className="py-6 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3">No contacts.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">Phone</th>
              <th className="text-left py-2">Primary</th>
              <th className="text-right py-2 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, idx) => (
              <tr key={`${c.name}-${c.createdAt ?? idx}`} className="border-b last:border-0" data-testid={`contact-row-${idx}`}>
                <td className="py-2">{c.name}</td>
                <td className="py-2">{c.email || '--'}</td>
                <td className="py-2">{c.phone ? formatPhoneDisplay(c.phone) : '--'}</td>
                <td className="py-2">
                  {c.isPrimary ? (
                    <span
                      data-testid={`contact-primary-badge-${idx}`}
                      className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"
                    >
                      Primary
                    </span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="cursor-pointer"
                    data-testid={`contact-edit-${idx}`}
                    onClick={() => { setEditIdx(idx); setDialogOpen(true); }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="cursor-pointer"
                    data-testid={`contact-delete-${idx}`}
                    onClick={() => setDeleteIdx(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ContactFormDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditIdx(null); }}
        initial={editIdx !== null ? contacts[editIdx] : undefined}
        onSubmit={handleAddOrEditSubmit}
        isSaving={replace.isPending}
      />

      <AlertDialog open={deleteIdx !== null} onOpenChange={(o) => { if (!o) setDeleteIdx(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this contact from the insured. The change is
              forwarded to the Data Center.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="contact-delete-confirm" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
